import { useCallback, useMemo } from 'react';
import {
  useNavigate,
  useLocation,
  useSearchParams as useRRSearchParams,
  useParams as useRRParams,
} from 'react-router';
import { sanitizeUrlParam, isRelativePath } from '@/lib/utils';

export function useRouter() {
  const navigate = useNavigate();
  const location = useLocation();

  const push = useCallback(
    (path: string, options?: { state?: unknown }) => navigate(path, options),
    [navigate],
  );
  const replace = useCallback((path: string) => navigate(path, { replace: true }), [navigate]);
  const back = useCallback(() => navigate(-1), [navigate]);
  const forward = useCallback(() => navigate(1), [navigate]);
  const refresh = useCallback(() => navigate(0), [navigate]);

  return useMemo(
    () => ({
      push,
      replace,
      back,
      forward,
      refresh,
      pathname: location.pathname,
    }),
    [push, replace, back, forward, refresh, location.pathname],
  );
}

export function usePathname() {
  const location = useLocation();
  return location.pathname;
}

export function useSearchParams() {
  const [searchParams] = useRRSearchParams();

  const sanitizedParams = useMemo(() => {
    return new Proxy(searchParams, {
      get(target, prop, receiver) {
        if (prop === 'get') {
          return (key: string) => {
            const value = target.get(key);
            const sanitized = sanitizeUrlParam(value);
            if (key === 'to' && sanitized !== null) {
              return isRelativePath(sanitized) ? sanitized : '/';
            }
            return sanitized;
          };
        }
        if (prop === 'getAll') {
          return (key: string) => target.getAll(key).map((v) => sanitizeUrlParam(v) ?? '');
        }
        return Reflect.get(target, prop, receiver);
      },
    }) as URLSearchParams;
  }, [searchParams]);

  return sanitizedParams;
}

export function useParams<T extends Record<string, string | undefined> = Record<string, string>>() {
  return useRRParams<T>();
}
